import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[#252360] text-white shadow-md hover:bg-[#4451A3] hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0",
        success:
              "bg-green-600 text-white shadow-md hover:bg-green-700 focus-visible:ring-green-500 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0",
        accent:
          "bg-[#E62067] text-white shadow-md hover:bg-[#db2777] hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0",
        outline:
          "border-2 border-[#252360] bg-transparent text-[#252360] shadow-sm hover:bg-[#252360] hover:text-white hover:shadow-md",
        secondary:
          "bg-gray-100 text-gray-900 shadow-sm hover:bg-gray-200 hover:shadow-md",
        ghost: "hover:bg-gray-100 hover:text-gray-900",
        link: "text-[#252360] underline-offset-4 hover:underline hover:text-[#4451A3]",
        gradient:
          "bg-gradient-to-r from-[#252360] via-[#4451A3] to-[#E62067] text-white shadow-lg hover:shadow-xl hover:-translate-y-1 active:translate-y-0",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-8 rounded-md px-4 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <div className="loading-spinner ml-2" />
        )}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants };
